// The date a client would like their shoot. An empty value means "decide later".
// Stored as YYYY-MM-DD, with no time zone, so it never shifts by a day.

export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Today in the studio's timezone, as YYYY-MM-DD plus minutes after midnight. */
export function studioTodayParts(now: Date = new Date()): { date: string; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const pick = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || '';
  let hour = Number(pick('hour'));
  if (hour === 24) hour = 0;
  const minute = Number(pick('minute'));
  return {
    date: `${pick('year')}-${pick('month').padStart(2, '0')}-${pick('day').padStart(2, '0')}`,
    minutes: hour * 60 + minute,
  };
}

/** A chosen start time on today's studio date that is no longer ahead. */
export function shootTimeHasPassed(date: string, time: string, now: Date = new Date()): boolean {
  if (!date || !time) return false;
  const studio = studioTodayParts(now);
  if (date !== studio.date) return false;
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return false;
  return Number(match[1]) * 60 + Number(match[2]) <= studio.minutes;
}

export function isValidPreferredDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return false;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.getUTCFullYear() === Number(match[1])
    && date.getUTCMonth() === Number(match[2]) - 1
    && date.getUTCDate() === Number(match[3]);
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Written out by hand so it reads the same in every browser: "Saturday 14 November 2026".
export function formatPreferredDate(value: string | null | undefined): string {
  if (!value || !isValidPreferredDate(value)) return '';
  const [y, m, d] = value.split('-').map(Number);
  const weekday = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${weekday} ${d} ${MONTHS[m - 1]} ${y}`;
}

/** One short line for messages and summaries. */
export function preferredDateLine(value: string | null | undefined): string {
  const label = formatPreferredDate(value);
  return label ? `Preferred shoot date: ${label}` : 'Preferred shoot date: not decided yet';
}
